import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Drawer, Stack, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PREVIEW_IGNORE_CLOSE_DATA_ATTR } from '@/hooks/usePreviewMode';
import { LeaveWithoutSavingDialog } from '@/components/dialogs/LeaveWithoutSavingDialog';
import { DRAWER_MAXIMISED_WIDTHS, DRAWER_TRANSITION, DRAWER_WIDTHS } from './constants';
import { FolioDrawerContext } from './FolioDrawerContext';
import { FolioDrawerHeader } from './FolioDrawerHeader';

// Escape closes only the most recently opened drawer.
const openDrawerKeyHandlers = [];

/**
 * Shared right-hand drawer shell used by every Folio drawer.
 *
 * Renders a persistent MUI Drawer with a fixed-width panel, an optional pinned
 * {@link FolioDrawerHeader} and a scrolling content area. A click outside or Escape
 * shows a "Leave without saving?" confirm when `confirmClose` is set or a
 * {@link FolioDrawerFormGuard} inside reports a dirty form. The header's close button
 * always closes straight away.
 *
 * Children that render their own header (forms with their own scroll area and
 * footer) omit `title` and render `<FolioDrawerHeader />` themselves; close and
 * maximise state reach them through {@link FolioDrawerContext}.
 *
 * @param {object} props
 * @param {boolean} props.open Whether the drawer is open.
 * @param {(event?: Event) => void} props.onClose Closes the drawer. Called directly by the
 *   header's close button, and after any confirm for a click outside or Escape.
 * @param {string} [props.title] Header title. When omitted no header is rendered and the
 *   content area has no padding, so the child owns its layout.
 * @param {React.ReactNode} [props.icon] Icon shown beside the title.
 * @param {React.ReactNode} [props.headerActions] Extra controls on the right of the header.
 * @param {'wide' | 'narrow'} [props.size='wide'] Panel width; see `DRAWER_WIDTHS`.
 * @param {boolean} [props.canMaximise=false] Shows a toggle that expands the panel.
 * @param {'reduced' | 'full'} [props.maximisedSize='reduced'] Width when maximised; see
 *   `DRAWER_MAXIMISED_WIDTHS`. Use `full` only for editors that need the whole screen.
 * @param {boolean} [props.backdropClose=true] Close on a click outside the panel or on Escape.
 *   Set to false for side panels that leave the page usable, such as Preview.
 * @param {boolean} [props.blurBackdrop=false] Tints and blurs the page beside the panel, as Ask AI
 *   does. Only applies with `backdropClose`, since it styles the click-outside area.
 * @param {boolean} [props.confirmClose=false] Confirm before closing on a click outside or Escape,
 *   e.g. `confirmClose={dirty}`.
 * @param {object} [props.slotProps] Props passed through to inner elements.
 * @param {object} [props.slotProps.paper] Props for the MUI Drawer paper; `sx` is merged.
 * @param {object} [props.slotProps.backdrop] Props for the click-outside Box; `sx` is merged.
 * @param {object} [props.slotProps.content] Props for the scrolling content Box; `sx` is merged.
 * @param {object} [props.slotProps.confirmDialog] Props overriding the leave-confirm
 *   (`LeaveWithoutSavingDialog`), e.g. its title or content.
 * @param {React.ReactNode} props.children Drawer content.
 */
export const FolioDrawer = ({
	open,
	onClose,
	title,
	icon,
	headerActions,
	size = 'wide',
	canMaximise = false,
	maximisedSize = 'reduced',
	backdropClose = true,
	blurBackdrop = false,
	confirmClose = false,
	slotProps = {},
	children,
}) => {
	const theme = useTheme();
	const [isMaximised, setIsMaximised] = useState(false);
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);

	// Refs so a deferred close decision reads the latest values, not the ones from its render.
	const isFormDirtyRef = useRef(false);
	const confirmCloseRef = useRef(confirmClose);
	confirmCloseRef.current = confirmClose;
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;

	const setIsFormDirty = useCallback((isDirty) => {
		isFormDirtyRef.current = isDirty;
	}, []);

	// Blur first so fields that only commit to Formik on blur update the form, then decide on
	// the next tick once that update has rendered and FolioDrawerFormGuard has reported it.
	const requestClose = useCallback((event) => {
		document.activeElement?.blur();
		setTimeout(() => {
			if (confirmCloseRef.current || isFormDirtyRef.current) {
				setIsConfirmOpen(true);
				return;
			}
			onCloseRef.current(event);
		}, 0);
	}, []);

	useEffect(() => {
		if (!open) setIsMaximised(false);
	}, [open]);

	useEffect(() => {
		if (!open || !backdropClose) return;

		const handleKeyDown = (event) => {
			if (event.key !== 'Escape' || event.defaultPrevented) return;
			if (openDrawerKeyHandlers.at(-1) !== handleKeyDown) return;
			requestClose(event);
		};

		openDrawerKeyHandlers.push(handleKeyDown);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			openDrawerKeyHandlers.splice(openDrawerKeyHandlers.indexOf(handleKeyDown), 1);
		};
	}, [open, backdropClose, requestClose]);

	// The header's close button is a deliberate close, so it skips the leave-confirm.
	const closeNow = useCallback((event) => onCloseRef.current(event), []);

	const contextValue = useMemo(
		() => ({
			onClose: closeNow,
			canMaximise,
			isMaximised,
			setIsMaximised,
			setIsFormDirty,
		}),
		[closeNow, canMaximise, isMaximised, setIsFormDirty],
	);

	const panelWidth = isMaximised ? DRAWER_MAXIMISED_WIDTHS[maximisedSize] : DRAWER_WIDTHS[size];

	return (
		<>
			<Drawer
				open={open}
				onClose={requestClose}
				anchor='right'
				variant='persistent'
				transitionDuration={DRAWER_TRANSITION}
				slotProps={{
					paper: {
						[PREVIEW_IGNORE_CLOSE_DATA_ATTR]: '',
						...slotProps.paper,
						sx: {
							width: backdropClose ? '100vw' : panelWidth,
							flexDirection: 'row',
							overflow: 'visible',
							border: 'none',
							backgroundColor: 'transparent',
							...slotProps.paper?.sx,
						},
					},
				}}
			>
				{backdropClose && (
					<Box
						{...slotProps.backdrop}
						onClick={requestClose}
						sx={{
							flex: 1,
							...(blurBackdrop && {
								background: `linear-gradient(to left, ${alpha(theme.palette.common.black, 0.28)}, transparent)`,
								backdropFilter: open ? 'blur(2px)' : 'blur(0px)',
								opacity: open ? 1 : 0,
								transition: theme.transitions.create(['opacity', 'backdrop-filter'], {
									duration: open ? DRAWER_TRANSITION.enter : DRAWER_TRANSITION.exit,
								}),
							}),
							...slotProps.backdrop?.sx,
						}}
					/>
				)}
				<Stack
					sx={{
						width: panelWidth,
						maxWidth: isMaximised ? '100vw' : '1400px',
						height: '100vh',
						flexShrink: 0,
						backgroundColor: theme.palette.background.paper,
						boxShadow: '-2px 0px 20px rgba(0,0,0,0.2)',
						transition: theme.transitions.create(['width', 'max-width'], {
							duration: theme.transitions.duration.standard,
						}),
					}}
				>
					<FolioDrawerContext.Provider value={contextValue}>
						{title && <FolioDrawerHeader {...{ title, icon, headerActions }} />}
						<Box
							{...slotProps.content}
							sx={{
								flex: 1,
								minHeight: 0,
								display: 'flex',
								flexDirection: 'column',
								overflowY: 'auto',
								...(title && { px: 2 }),
								...slotProps.content?.sx,
							}}
						>
							{children}
						</Box>
					</FolioDrawerContext.Provider>
				</Stack>
			</Drawer>
			<LeaveWithoutSavingDialog
				open={isConfirmOpen}
				onClose={() => setIsConfirmOpen(false)}
				onConfirm={() => {
					setIsConfirmOpen(false);
					onClose();
				}}
				{...slotProps.confirmDialog}
			/>
		</>
	);
};

export default FolioDrawer;
